import "isomorphic-fetch";
import config from "./config";
import _ from "lodash";
import { sessionService } from "redux-react-session";
import axiosInstance from "../services/auth/axiosInstance";
import fetchWithJWT from "../services/auth/fetchWithJWT";
import _config from "../config";
import Cookies from 'js-cookie';
import { validateRedirectUrl } from "../utilities/redirectValidation";
import { handleTwoFaResponse } from "../utils/2fa-token.service";

// Constants
const HTTP_STATUS = {
  SUCCESS: 200,
  UNAUTHORIZED: 401,
  SERVER_ERROR: 500,
};

const USER_ROLES = {
  OSC_FIRM: 2,
  CEC_FIRM: 15,
};

const TOKEN_TTL_HOURS = 8;
const DEFAULT_REDIRECT_PATH = '/projects';
const PRE_LOGIN_PATH = '/preLogin';

/**
 * Clears authentication-related data from localStorage
 */
const clearAuthStorage = () => {
  const keysToRemove = [
    config.SSO_Session_Idx,
    config.SSO_user_name,
    config.SSO_user_adGroups,
    config.SSO_user_loginId,
    config.SSO_signOutUrl,
    config.SSO_signInUrl,
    config.SSO_token_ttl,
    config.fetch_details_param,
    "alertCount",
  ];

  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove ${key} from localStorage:`, error);
    }
  });
};

/**
 * Extracts session index from response profile
 * Handles both string and object formats
 */
const extractSessionIndex = (profile) => {
  if (typeof profile.sessionIndex === 'string') {
    return profile.sessionIndex;
  }
  
  if (typeof profile.sessionIndex === 'object' && profile.sessionIndex !== null) {
    return profile.nameID || profile.sessionIndex.sessionId || JSON.stringify(profile.sessionIndex);
  }
  
  return profile.nameID || `session-${Date.now()}`;
};

/**
 * Extracts user name from profile based on user type
 */
const extractUserName = (profile) => {
  const isExternalUser = profile.extUserInfo && profile.extUserInfo.length > 0;
  
  if (isExternalUser) {
    const givenName = profile.sessionIndex?.name?.givenName || '';
    const familyName = profile.sessionIndex?.name?.familyName || '';
    const fullName = `${givenName} ${familyName}`.trim();
    
    return fullName || 
           profile.givenName || 
           profile.nameID || 
           profile.extUserInfo[0]?.sys_userid || 
           'Unknown User';
  }
  
  // Internal user
  return profile.sn 
    ? `${profile.givenName} ${profile.sn}` 
    : profile.givenName || 'Unknown User';
};

/**
 * Sets external user information in storage
 */
const setExternalUserInfo = (profile) => {
  if (!profile.extUserInfo || profile.extUserInfo.length === 0) {
    return null;
  }

  const authStatus = profile.authState;
  profile.extUserInfo[0].authStatus = authStatus;

  const extUser = profile.extUserInfo[0];
  Cookies.set(config.SSO_extUser_Ind, extUser.external_user_ind);

  const roleID = extUser.role_id;
  const extRole = roleID === USER_ROLES.OSC_FIRM ? ["osc firm"] : ["cec firm"];
  localStorage.setItem(config.SSO_user_adGroups, extRole);
  
  return extUser;
};

/**
 * Handles successful authentication response
 * Sets up user session and stores authentication data
 */
const handleSuccessResponse = async (res) => {
  localStorage.setItem("alertCount", 0);
  Cookies.set(config.SSO_extUser_Ind, 0);

  const sessionIndex = extractSessionIndex(res.profile);
  localStorage.setItem(config.SSO_Session_Idx, sessionIndex);
  
  const userName = extractUserName(res.profile);
  localStorage.setItem(config.SSO_user_name, userName);
  localStorage.setItem(config.SSO_user_loginId, res.profile?.nameID);
  localStorage.setItem(config.SSO_signOutUrl, res.signOutUrl);
  
  const ttl = new Date();
  ttl.setHours(ttl.getHours() + TOKEN_TTL_HOURS);
  localStorage.setItem(config.SSO_token_ttl, ttl.getTime());

  const extUser = setExternalUserInfo(res.profile);
  
  if (!extUser) {
    localStorage.setItem(config.SSO_user_adGroups, res.profile.memberOf);
  }

  const user = {
    sesIdx: sessionIndex,
    data: {
      name: userName,
      loginId: res.profile?.nameID,
      userRole: localStorage.getItem(config.SSO_user_adGroups),
      extUserInfo: res.profile.extUserInfo,
    },
  };

  await sessionService.saveSession({ sesIdx: sessionIndex });
  return sessionService.saveUser(user);
};

/**
 * Handles unauthorized response by redirecting to appropriate sign-in page
 */
const handleUnauthorizedResponse = (res) => {
  const fetchDetailsParam = localStorage.getItem(config.fetch_details_param);
  
  if (fetchDetailsParam === null) {
    window.location = PRE_LOGIN_PATH;
    return;
  }
  
  if (fetchDetailsParam === "false") {
    let ssoSignInUrl = res.signInUrl || process.env.SSO_FALLBACK_SIGN_IN_URL;
    
    if (!ssoSignInUrl || ssoSignInUrl.includes("undefined")) {
      ssoSignInUrl = process.env.SSO_FALLBACK_SIGN_IN_URL;
    }
    
    const url = new URL(ssoSignInUrl, window.location.origin);
    url.searchParams.set('jwt', 'true');
    
    localStorage.setItem(config.SSO_signInUrl, url.toString());
    window.location = url.toString();
  }
};

/**
 * Fetches user details from backend using cookie-based authentication
 * @param {boolean|string} extUser - Flag indicating if user is external
 * @returns {Promise} Promise resolving to user details
 */
const fetchDetails = (extUser) => {
  return new Promise((resolve, reject) => {
    const url = `${_config.BASE_URL}fetchDetails?extUser=${extUser}`;
    
    fetchWithJWT(url)
      .then((response) => response.json())
      .then(async (res) => {
        if (res.status === HTTP_STATUS.SUCCESS) {
          try {
            await handleSuccessResponse(res);
          } catch (error) {
            console.error("Failed to save session data:", error);
          }
        } else if (res.status === HTTP_STATUS.UNAUTHORIZED) {
          handleUnauthorizedResponse(res);
        }
        resolve(res);
      })
      .catch((err) => {
        clearAuthStorage();
        reject(err);
      });
  });
};

/**
 * Checks if stored authentication token is still valid
 * @returns {boolean} True if token is valid
 */
const isTokenValid = () => {
  const ls_ttl = parseInt(localStorage.getItem(config.SSO_token_ttl), 10);
  return ls_ttl && new Date() <= new Date(ls_ttl);
};

/**
 * Signs in user with existing session or fetches new details
 * @param {boolean|string} extUser - Flag indicating if user is external
 * @returns {Promise} Promise resolving to authentication status
 */
const signIn = (extUser) => {
  return new Promise((resolve, reject) => {
    const sessionIndex = localStorage.getItem(config.SSO_Session_Idx);
    if (_.isEmpty(sessionIndex)) {
      fetchDetails(extUser)
        .then(() => resolve({ status: "success", message: "User details fetched successfully" }))
        .catch((err) => {
          signOut();
          reject(err);
        });
      return;
    }

    if (isTokenValid()) {
      resolve({ status: "success", message: "User already authenticated" });
    } else {
      // Try to refresh session if session data exists
      fetchDetails(extUser)
        .then(() => resolve({ status: "success", message: "Session refreshed successfully" }))
        .catch((err) => {
          signOut();
          reject(err);
        });
    }
  });
};

/**
 * Signs out user by clearing session and redirecting to pre-login
 * @returns {Promise} Promise resolving when sign-out is complete
 */
const signOut = () => {
  sessionService.deleteSession();
  sessionService.deleteUser();
  clearAuthStorage();

  return new Promise((resolve, reject) => {
    fetchWithJWT(`${_config.BASE_URL}logout`, { method: 'GET' })
      .then(() => {
        window.location = PRE_LOGIN_PATH;
        resolve({ status: "success" });
      })
      .catch((err) => {
        console.error("Logout error:", err);
        window.location = PRE_LOGIN_PATH;
        reject(err);
      });
  });
};

/**
 * Redirects user to sign-in URL with session timeout alert
 * @param {string} signInUrl - Optional sign-in URL to use instead of localStorage value
 */
const redirectToSignInUrl = (signInUrl) => {
  const alertCount = localStorage.getItem("alertCount");
  if (alertCount === "0") {
    localStorage.setItem("alertCount", 1);
    alert("Your session timed out. All your unsaved progress would be lost.");
  }
  
  const localStor_singInUrl = localStorage.getItem(config.SSO_signInUrl);
  const extUserFlag = localStorage.getItem(config.fetch_details_param);
  
  sessionService.deleteSession();
  sessionService.deleteUser();
  localStorage.clear();
  
  // Use provided signInUrl if available, otherwise fall back to localStorage or default
  const redirectUrl = signInUrl || (extUserFlag ? PRE_LOGIN_PATH : localStor_singInUrl);
  
  if (redirectUrl && redirectUrl !== PRE_LOGIN_PATH) {
    try {
      const url = new URL(redirectUrl, window.location.origin);
      url.searchParams.set('jwt', 'true');
      window.location = url.toString();
      return;
    } catch (e) {
      console.warn('Could not parse redirect URL:', e);
    }
  }
  
  window.location = redirectUrl || PRE_LOGIN_PATH;
};

/**
 * Signs in external users via API with username and password
 * @param {string} username - User's login username
 * @param {string} password - User's password
 * @returns {Promise} Promise resolving to authentication response
 */
const signInExternalUsers = (username, password) => {
  // Clear previous session data
  try {
    localStorage.removeItem(config.SSO_Session_Idx);
    localStorage.removeItem(config.SSO_user_name);
    localStorage.removeItem(config.SSO_user_adGroups);
    localStorage.removeItem(config.SSO_user_loginId);
    localStorage.removeItem(config.SSO_signOutUrl);
    localStorage.removeItem(config.SSO_signInUrl);
    localStorage.removeItem(config.SSO_token_ttl);
    localStorage.removeItem("alertCount");
  } catch (error) {
    console.error("Error clearing previous session:", error);
  }

  const payload = {
    username,
    password,
    jwt: 'true'
  };

  return axiosInstance
    .post("loginExtUser", payload)
    .then(async (res) => {
      // ← NEW: Handle 2FA requirement (stateless challenge token model)
      if (res.data.status === 'TWOFA_REQUIRED') {
        // User password verified but needs OTP verification
        // Store challenge token and return 2FA indicator
        const twoFaResult = handleTwoFaResponse(res.data);
        
        if (!twoFaResult.success) {
          return {
            status: 500,
            error: twoFaResult.error || 'Failed to process 2FA'
          };
        }

        // Return 2FA required indicator with challenge token info
        return {
          status: 'TWOFA_REQUIRED',
          requiresOTP: true,
          challengeToken: twoFaResult.challengeToken,
          remainingAttempts: twoFaResult.remainingAttempts,
          message: twoFaResult.message
        };
      }

      // ← EXISTING: Handle successful authentication (no 2FA)
      if (res.data.status === HTTP_STATUS.SUCCESS) {
        // Handle user profile data
        if (res.data.profile) {
          await handleSuccessResponse(res.data);
        } else {
          // Fetch profile using cookies
          await fetchDetails(true);
        }
       
        // Validate redirect URL from backend response
        let redirectURL = res.data.redirectURL;
        const safeRedirectURL = validateRedirectUrl(redirectURL, DEFAULT_REDIRECT_PATH);
        
        window.location.href = safeRedirectURL;
      } else {
        return res.data;
      }
    })
    .catch((err) => {
      console.error('External user login error:', err);
      return {
        status: HTTP_STATUS.SERVER_ERROR,
        error: "An error occurred while trying to login!"
      };
    });
};

export default {
  fetchDetails,
  signIn,
  signOut,
  redirectToSignInUrl,
  signInExternalUsers,
};
